import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type Column } from '@/components/shared/DataTable';
import {
  supplyService,
  type ProductionWeekPurchaseRow,
} from '@/services/supplyService';
import {
  isoWeekParts,
  parseIsoDateLocal,
} from '@/utils/productionScope';

interface ProductionWeekPurchasesPanelProps {
  productionWeek?: string;
  soExternalId?: string;
  scopeSupplyTypeLabel: string;
  warehouseId?: number | null;
  /** Cambia tras marcar L como gestionado: si el panel está abierto, se relee. */
  refreshNonce?: number;
  /** Tras reabrir un producto (SUP-LOT-009): la tabla debe recargarse. */
  onReopened?: () => void | Promise<void>;
}

function formatQty(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value) ? String(value) : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function ProductionWeekPurchasesPanel({
  productionWeek,
  soExternalId,
  scopeSupplyTypeLabel,
  warehouseId,
  refreshNonce,
  onReopened,
}: ProductionWeekPurchasesPanelProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState<ProductionWeekPurchaseRow[] | null>(null);
  const [reopeningId, setReopeningId] = useState<number | null>(null);

  const soOnly = Boolean(soExternalId) && !productionWeek;
  const isoWeek = useMemo(() => {
    if (!productionWeek) return '';
    const monday = parseIsoDateLocal(productionWeek);
    return monday ? isoWeekParts(monday).isoWeek : productionWeek;
  }, [productionWeek]);
  const scopeNotice = t('supply.productionScope.scopeNotice', { supplyType: scopeSupplyTypeLabel });

  useEffect(() => {
    setOpen(false);
    setLoading(false);
    setError(false);
    setRows(null);
  }, [productionWeek, soExternalId, warehouseId]);

  const load = useCallback(async () => {
    if (typeof warehouseId !== 'number') return;
    setOpen(true);
    setLoading(true);
    setError(false);
    try {
      const response = await supplyService.getProductionWeekPurchases({
        warehouseId,
        productionWeek: productionWeek || undefined,
        soExternalId: soExternalId || undefined,
      });
      setRows(response.results || []);
    } catch {
      setError(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [productionWeek, soExternalId, warehouseId]);

  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    if (refreshNonce && openRef.current) void load();
    // Solo al cambiar el nonce; el cambio de alcance cierra el panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce]);

  const reopen = useCallback(async (productId: number) => {
    setReopeningId(productId);
    try {
      await supplyService.markProductionScopeManaged({
        productIds: [productId],
        productionWeek: productionWeek || undefined,
        soExternalId: soExternalId || undefined,
        managed: false,
      });
      await load();
      await onReopened?.();
    } catch {
      alert(t('supply.productionScope.purchases.reopenError'));
    } finally {
      setReopeningId(null);
    }
  }, [load, onReopened, productionWeek, soExternalId, t]);

  const columns = useMemo<Column[]>(() => [
    {
      header: t('supply.table.code'),
      accessor: 'code',
      sortable: true,
      width: '78px',
    },
    {
      header: t('supply.table.product'),
      accessor: 'name',
      sortable: true,
      width: '190px',
    },
    {
      header: t('supply.productionScope.purchases.ordered'),
      accessor: 'qty_ordered',
      sortable: true,
      numeric: true,
      align: 'right',
      width: '88px',
      cell: (value: number) => formatQty(value),
    },
    {
      header: t('supply.productionScope.purchases.received'),
      accessor: 'qty_received',
      sortable: true,
      numeric: true,
      align: 'right',
      width: '88px',
      cell: (value: number) => formatQty(value),
    },
    {
      header: t('supply.table.stockInTransit'),
      accessor: 'qty_in_transit',
      sortable: true,
      numeric: true,
      align: 'right',
      width: '88px',
      cell: (value: number) => formatQty(value),
    },
    {
      header: t('supply.productionScope.inPendingOrder'),
      accessor: 'qty_pending_send',
      sortable: true,
      numeric: true,
      align: 'right',
      width: '110px',
      cell: (value: number) => formatQty(value),
    },
    {
      header: t('supply.productionScope.purchases.markedManaged'),
      accessor: 'qty_marked_managed',
      sortable: true,
      numeric: true,
      align: 'right',
      width: '110px',
      cell: (value: number | undefined) => formatQty(value ?? 0),
    },
    {
      header: t('supply.productionScope.purchases.salesOrders'),
      accessor: 'sales_orders',
      width: '160px',
      cell: (_value, item: ProductionWeekPurchaseRow) => {
        const labels = (item.sales_orders || [])
          .map((so) => so.external_id)
          .filter(Boolean);
        return labels.length ? labels.join(', ') : '—';
      },
    },
    {
      header: t('supply.productionScope.purchases.lastDate'),
      accessor: 'last_order_date',
      sortable: true,
      width: '110px',
      cell: (value: string | null) => {
        if (!value) return '—';
        return new Date(`${value}T00:00:00`).toLocaleDateString(i18n.language);
      },
    },
    {
      header: t('supply.productionScope.purchases.status'),
      accessor: 'status',
      width: '160px',
      cell: (_value, item: ProductionWeekPurchaseRow) => (
        <div className="flex flex-wrap gap-1">
          {item.qty_in_transit > 0 ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              {t('supply.productionScope.purchases.inTransit')}
            </span>
          ) : null}
          {item.qty_received > 0 ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
              {t('supply.productionScope.purchases.receivedBadge')}
            </span>
          ) : null}
          {item.qty_pending_send > 0 ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {t('supply.productionScope.inPendingOrder')}
            </span>
          ) : null}
          {(item.qty_marked_managed ?? 0) > 0 ? (
            <>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {t('supply.productionScope.purchases.markedManaged')}
              </span>
              <button
                type="button"
                onClick={() => void reopen(item.product_id)}
                disabled={reopeningId !== null}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('supply.productionScope.purchases.reopen')}
              </button>
            </>
          ) : null}
        </div>
      ),
    },
    {
      header: t('supply.productionScope.purchases.orders'),
      accessor: 'purchases',
      width: '240px',
      cell: (_value, item: ProductionWeekPurchaseRow) => {
        if (!item.purchases?.length) return '—';
        return item.purchases.map((purchase, index) => {
          const dateLabel = purchase.order_date
            ? new Date(`${purchase.order_date}T00:00:00`).toLocaleDateString(i18n.language)
            : '';
          const sourceLabel = purchase.source === 'pending_send'
            ? t('supply.productionScope.inPendingOrder')
            : purchase.source === 'received'
              ? t('supply.productionScope.purchases.receivedBadge')
              : t('supply.productionScope.purchases.inTransit');
          return (
            <div key={`${purchase.order_number}-${index}`} className="text-xs text-gray-700">
              {purchase.order_number}
              {dateLabel ? ` · ${dateLabel}` : ''}
              {` · ${sourceLabel}`}
            </div>
          );
        });
      },
    },
  ], [i18n.language, reopen, reopeningId, t]);

  if (typeof warehouseId !== 'number') return null;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">
          {soOnly
            ? t('supply.productionScope.purchases.titleSo', { so: soExternalId })
            : t('supply.productionScope.purchases.title', { iso: isoWeek })}
        </h2>
        {!open ? (
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-[#1681ba] rounded-md hover:bg-[#126a9a] transition-colors shrink-0"
          >
            {soOnly
              ? t('supply.productionScope.purchases.openSo', { so: soExternalId })
              : t('supply.productionScope.purchases.open', { iso: isoWeek })}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setRows(null);
              setError(false);
            }}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shrink-0"
          >
            {t('supply.productionScope.purchases.close')}
          </button>
        )}
      </div>
      <p className="mb-4 text-sm text-indigo-900/80">
        {scopeNotice}
      </p>

      {open && loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1681ba] mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('supply.messages.loadingProducts')}</p>
          </div>
        </div>
      ) : null}

      {open && !loading && error ? (
        <p className="text-sm text-gray-600">
          {soOnly
            ? t('supply.productionScope.purchases.errorSo')
            : t('supply.productionScope.purchases.error')}
        </p>
      ) : null}

      {open && !loading && !error && rows && rows.length === 0 ? (
        <p className="text-sm text-gray-600">
          {soOnly
            ? t('supply.productionScope.purchases.emptySo')
            : t('supply.productionScope.purchases.empty')}
        </p>
      ) : null}

      {open && !loading && !error && rows && rows.length > 0 ? (
        <DataTable
          columns={columns}
          data={rows}
          compact
          resizableColumns
          allowExport={false}
        />
      ) : null}
    </div>
  );
}
